import {
  useHomeTodos,
  useMyRelationships,
  useNextConversation,
} from "@coaching-culture/api-client";
import {
  ContentType,
  ProductIdent,
  ProductVisibility,
} from "@coaching-culture/types";
import { useUser } from "auth";
import { useAllProductVisibility } from "hooks/useProductVisibility";
import { ContinueLearningCard } from "./ContinueLearningCard";
import { HomeHeader } from "./HomeHeader";
import { MyGoalsCard } from "./MyGoalsCard";
import { NextConversationCard } from "./NextConversationCard";
import { Area, Layout } from "./styles";
import { TodoList } from "./TodoList";

const contentProducts: [ProductIdent, ContentType][] = [
  ["mindset", "module"],
  ["lessons", "lesson"],
  ["assessments", "assessment"],
  ["learncast", "learncast"],
];

/**
 * The Home page (the "home-v2" product). One to-do list from every product,
 * the next conversation with a brief of what changed, goals and learning.
 * Orgs without the product still get the widget dashboard.
 */
export function Home() {
  const [user] = useUser();
  const products = useAllProductVisibility();
  const has = (ident: ProductIdent) =>
    products[ident] === ProductVisibility.Enabled;

  const hasPerformance = has("performance");
  const todos = useHomeTodos();
  const next = useNextConversation(hasPerformance);
  const { data: relationships } = useMyRelationships();

  const isManager =
    relationships?.some(
      (r) => r.type === "manager" && r.from.id === user.id && r.removed == null,
    ) ?? false;
  const canSchedule = hasPerformance && isManager;
  const enabledContent = contentProducts
    .filter(([p]) => has(p))
    .map(([, t]) => t);

  return (
    <Layout>
      <Area area="header">
        <HomeHeader
          name={user.name}
          todos={todos.data}
          next={next.data}
          canGiveFeedback={has("cont-feedback")}
          canSchedule={canSchedule}
        />
      </Area>
      <Area area="todo">
        <TodoList
          todos={todos.data}
          isLoading={todos.isLoading}
          isError={todos.isError}
          onRetry={() => todos.refetch()}
        />
      </Area>
      {hasPerformance && (
        <>
          <Area area="next">
            <NextConversationCard
              next={next.data}
              isLoading={next.isLoading}
              isError={next.isError}
              onRetry={() => next.refetch()}
              canSchedule={canSchedule}
            />
          </Area>
          <Area area="goals">
            <MyGoalsCard />
          </Area>
        </>
      )}
      {enabledContent.length > 0 && (
        <Area area="learning">
          <ContinueLearningCard enabledTypes={enabledContent} />
        </Area>
      )}
    </Layout>
  );
}
